import type {AddBlockedByInput} from '@github-ui/server-persisted-queries/types/createIssueAddBlockedByMutation'

export type AddBlockedByResult = {
  issueId: string
  blockingIssueId: string
} | null

export type AddBlockedBy = (input: AddBlockedByInput, onRequestStart?: () => void) => Promise<AddBlockedByResult>

/**
 * Create issue relationships after creation (fire-and-forget).
 *
 * Failures are reported but don't block issue creation success.
 * Matches project assignment pattern. This will be replaced when createIssue
 * mutation supports blocking/blockedBy parameters (tracked in github/github#412771).
 */
export async function handleIssueRelationships(
  addBlockedBy: AddBlockedBy,
  newIssueId: string,
  blockedByIssueIds: string[],
  blockingIssueIds: string[],
  onRequestsStarted?: () => void,
): Promise<void> {
  const inputs: AddBlockedByInput[] = [
    ...blockedByIssueIds.map(blockingIssueId => ({
      issueId: newIssueId,
      blockingIssueId,
      clientMutationId: undefined,
    })),
    ...blockingIssueIds.map(issueId => ({
      issueId,
      blockingIssueId: newIssueId,
      clientMutationId: undefined,
    })),
  ]

  let startedRequestCount = 0
  const results = await Promise.allSettled(
    inputs.map(input => {
      if (!onRequestsStarted) return addBlockedBy(input)

      let requestStarted = false
      const markRequestStarted = () => {
        if (requestStarted) return

        requestStarted = true
        startedRequestCount += 1
        if (startedRequestCount === inputs.length) {
          onRequestsStarted()
        }
      }

      return (async () => {
        try {
          return await addBlockedBy(input, markRequestStarted)
        } finally {
          markRequestStarted()
        }
      })()
    }),
  )
  const failedCount = results.filter(result => result.status === 'rejected' || !result.value).length

  if (failedCount > 0) {
    setTimeout(() => {
      throw new Error(`Failed to add ${failedCount} of ${inputs.length} issue relationship(s)`)
    })
  }
}
